import React from 'react';
import { motion } from 'framer-motion';
import SEO from './SEO';
import { FileText, ShieldAlert, DollarSign, Globe, CheckCircle2, Scale } from 'lucide-react';

const TermsAndConditions = () => {
  return (
    <div className="bg-[#FAF9F6] min-h-screen pt-24 pb-20">
      <SEO 
        title="Terms & Conditions" 
        description="Review the terms and conditions of service for Sunshine Travels. Understand your rights, booking policies, cancellations, and travel rules." 
        canonicalPath="/terms-and-conditions" 
      />
      {/* Header Section */}
      <section className="bg-slate-900 text-white py-20 mb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto space-y-6"
          >
            <FileText className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h1 className="text-4xl md:text-6xl font-serif font-bold tracking-wide">Terms & <span className="text-orange-400">Conditions.</span></h1>
            <p className="text-gray-400 text-lg font-medium leading-relaxed">
              Please read these terms and conditions carefully before booking. They govern our relationship with you for all bookings and services provided.
            </p>
            <p className="text-xs uppercase tracking-widest text-gray-500 font-bold">Last Updated: June 11, 2026</p>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Section 1: Acceptance of Terms */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white p-10 rounded-[2.5rem] shadow-sm border border-slate-100"
          >
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 bg-orange-55/10 text-orange-500 rounded-2xl flex items-center justify-center shrink-0">
                <CheckCircle2 size={24} />
              </div>
              <div className="space-y-4">
                <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">1. Agreement to Terms</h2>
                <p className="text-slate-600 leading-relaxed font-medium">
                  By accessing our website and utilizing our travel booking services, you agree to be bound by these Terms & Conditions. If you do not agree with any part of these terms, you must not proceed with booking or using our services.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Section 2: Bookings & Payments */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white p-10 rounded-[2.5rem] shadow-sm border border-slate-100"
          >
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 bg-slate-900 text-white rounded-2xl flex items-center justify-center shrink-0">
                <DollarSign size={24} />
              </div>
              <div className="space-y-4">
                <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">2. Bookings, Rates & Payments</h2>
                <p className="text-slate-600 leading-relaxed font-medium">
                  All bookings are subject to availability at the time of processing. Prices quoted are based on tariffs, exchange rates, and fuel charges active at the time of quotation and are subject to change until full payment is received.
                </p>
                <ul className="space-y-3 text-sm text-slate-500 font-medium list-disc pl-5">
                  <li>A non-refundable deposit is required at the time of booking confirmation.</li>
                  <li>Full payment must be cleared by the specified deadline prior to departure.</li>
                  <li>Failure to complete payments on time may result in automatic cancellation of bookings.</li>
                  <li>We accept major credit cards, bank transfers, and verified secure payment gateways.</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Section 3: Cancellations & Refunds */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white p-10 rounded-[2.5rem] shadow-sm border border-slate-100"
          >
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center shrink-0">
                <ShieldAlert size={24} />
              </div>
              <div className="space-y-4">
                <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">3. Cancellation & Refund Policy</h2>
                <p className="text-slate-600 leading-relaxed font-medium">
                  Cancellation policies vary by hotel, airline, and package. Any cancellation request must be submitted in writing. Standard processing timelines apply to all eligible refund requests.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  {[
                    "Airline cancellation charges apply as per carrier rules.",
                    "Hotel cancellation terms vary by booking class.",
                    "Service fees charged by Sunshine Travels are non-refundable.",
                    "Refunds are processed back to the original payment source."
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3 text-sm font-bold text-slate-500">
                      <CheckCircle2 size={16} className="text-orange-500" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 4: Travel Documents */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white p-10 rounded-[2.5rem] shadow-sm border border-slate-100"
          >
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 bg-blue-50 text-blue-500 rounded-2xl flex items-center justify-center shrink-0">
                <Globe size={24} />
              </div>
              <div className="space-y-4">
                <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">4. Passports, Visas & Health</h2>
                <p className="text-slate-600 leading-relaxed font-medium">
                  It is the traveler's sole responsibility to ensure they hold valid travel documents matching the destination country requirements.
                </p>
                <ul className="space-y-3 text-sm text-slate-500 font-medium list-disc pl-5">
                  <li>Passports must be valid for at least 6 months beyond the intended return date.</li>
                  <li>Required visas and entry permits must be secured prior to departure.</li>
                  <li>Compliance with local health advisories, vaccines, and quarantine guidelines is required.</li>
                  <li>Sunshine Travels is not liable for border entry denials or document non-compliance.</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Section 5: Liability Limitations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-slate-900 p-10 rounded-[2.5rem] shadow-2xl text-white relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl"></div>
            <div className="flex items-start gap-6 relative z-10">
              <div className="w-12 h-12 bg-orange-500 text-white rounded-2xl flex items-center justify-center shrink-0">
                <Scale size={24} />
              </div>
              <div className="space-y-4">
                <h2 className="text-2xl font-black uppercase tracking-tight">5. Limitation of Liability</h2>
                <p className="text-gray-400 leading-relaxed font-medium">
                  Sunshine Travels acts as an agent for airlines, hotels, transport operators, and other independent contractors. We are not responsible for injuries, damages, losses, delays, or schedule changes due to acts of God, mechanical failures, strikes, local laws, or other unforeseen events beyond our direct control.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Contact Section */}
          <div className="text-center py-10 space-y-4">
            <h3 className="text-xl font-bold text-slate-900 uppercase">Need clarification?</h3>
            <p className="text-slate-500 text-sm font-medium">Please reach out to our booking operations team at terms@sunshinetravels.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
